import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const LINES = ["CHARPENTE", "COUVERTURE", "BARDAGE"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="accueil" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src="/realisations/hero.jpg"
          alt="Bâtiment en bardage noir et charpente bois réalisé par Goujon Vallée en Normandie"
          className="w-full h-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-black/50 to-black/30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 min-h-screen flex flex-col justify-end max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-6"
        >
          <div className="flex gap-2 mb-4" data-testid="hero-brand-bars">
            <span className="h-1.5 w-16 bg-jaune" />
            <span className="h-1.5 w-16 bg-vert" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-jaune font-bold" data-testid="hero-overline">
            L'assurance d'un projet réussi — Artisans en Normandie depuis plus de 20 ans
          </p>
        </motion.div>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.88] text-[13vw] sm:text-7xl lg:text-[6.5rem]" data-testid="hero-title">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.15, ease: [0.33, 1, 0.68, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="max-w-md text-base text-zinc-300 leading-relaxed font-light"
            data-testid="hero-subtitle"
          >
            La couverture est la couronne de votre maison. Depuis plus de vingt ans,
            nous la taillons avec l'exigence des compagnons normands.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="flex items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-devis"
              className="group flex items-center gap-2 bg-white text-black px-7 py-4 text-xs tracking-[0.15em] uppercase font-bold hover:bg-jaune transition-colors duration-300"
            >
              Devis gratuit
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#realisations"
              data-testid="hero-cta-realisations"
              className="flex items-center gap-2 border border-white/30 px-7 py-4 text-xs tracking-[0.15em] uppercase font-bold hover:border-jaune hover:text-jaune transition-colors duration-300"
            >
              Nos réalisations
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-12 pt-8 border-t border-white/10 flex flex-wrap items-center gap-3"
          data-testid="hero-certifications"
        >
          <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mr-2">Certifications & gages de confiance</span>
          {["RGE", "Qualibat", "Éligible MaPrimeRénov'", "Plus de 20 ans d'expérience"].map((b, i) => (
            <span
              key={b}
              data-testid={`hero-cert-${i}`}
              className={`border px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold ${
                i % 2 === 0 ? "border-jaune/60 text-jaune" : "border-vert/70 text-vert"
              }`}
            >
              {b}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-10 z-10 hidden md:flex flex-col items-center gap-3 text-zinc-500"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase [writing-mode:vertical-rl]">Défiler</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
}
